// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartsDataView } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCarts from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCarts";
import updateSearchFields from "@insite/client-framework/Store/Pages/SavedOrderList/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xValidateOnSubmit } from "@steiner/XDL/Utilities";
import debounce from "lodash/debounce";
import React, { ChangeEvent, FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const StyledForm = getStyledWrapper("form");
const formId = "saveOrderName";

const mapStateToProps = (state: ApplicationState) => ({
    getCartsApiParameter: state.pages.savedOrderList.getCartsApiParameter,
    savedCartsDataView: getCartsDataView(state, state.pages.savedOrderList.getCartsApiParameter),
});

const mapDispatchToProps = {
    loadCarts,
};

interface SavedOrderNameModalProps {
    modalIsOpen?: boolean;
    onCancel: () => void;
    onSubmit: (savedOrderName: string, savedOrderId?: string) => void;
}

type Props = SavedOrderNameModalProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface SavedOrderNameModalStyles {
    modal?: ModalPresentationProps;
    gridContainer?: GridContainerProps;
    orderNameTextField?: InjectableCss;
    gridItem?: GridItemProps;
    buttonsGridItem?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    saveButton?: ButtonPresentationProps;
    listsSelect?: DynamicDropdownPresentationProps;
}

const savedOrderNameModalStyles: SavedOrderNameModalStyles = {
    modal: {
        sizeVariant: "small",
    },
    gridItem: {
        width: 12,
    },
    buttonsGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    cancelButton: {
        variant: "tertiary",
        css: css`
            margin-right: 10px;
        `,
    },
};

const styles = savedOrderNameModalStyles;
const SavedOrderNameModal: FC<Props> = ({
    modalIsOpen,
    onSubmit,
    onCancel,
    getCartsApiParameter,
    savedCartsDataView,
    loadCarts,
}) => {
    const [savedOrderName, setSavedOrderName] = useState("");
    const [inProgress, setInProgress] = useState(false);
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [selectListError, setSelectListError] = useState<React.ReactNode>("");
    const [selectedCartId, setSelectedCartId] = useState("new");

    useEffect(() => {
        if (!savedCartsDataView.value && !savedCartsDataView.isLoading) {
            loadCarts({ apiParameter: getCartsApiParameter });
        }
    });

    useEffect(() => {
        if (savedCartsDataView.value) {
            let options: OptionObject[] = [{ optionText: translate("Create New Saved Order"), optionValue: "new" }];
            options = options.concat(
                savedCartsDataView.value.map(o => ({ optionText: o.poNumber, optionValue: o.id })),
            );
            setOptions(options);
        }
    }, [savedCartsDataView]);

    const listChangeHandler = (value?: string) => {
        setSelectedCartId(value || "new");
        setSelectListError("");
    };

    const debouncedUpdateParameter = debounce((value: string) => {
        updateSearchFields({
            savedCartName: value,
        } as any);
    }, 200);

    const listsInputChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        debouncedUpdateParameter(event.currentTarget.value);
    };

    const onChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setSavedOrderName(event.target.value);
    };

    const modalCloseHandler = (e: any) => {
        setSavedOrderName("");
        setSelectListError("");
        setInProgress(false);
        onCancel?.();
    };

    const onSubmitHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const errors = xValidateOnSubmit(formId);
        if (errors.length) {
            return false;
        }

        onSubmit?.(savedOrderName, selectedCartId !== "new" ? selectedCartId : undefined);
    };

    return (
        <Modal
            headline={translate("Saved Order")}
            isOpen={modalIsOpen}
            handleClose={modalCloseHandler}
            {...styles.modal}
        >
            <StyledForm noValidate onSubmit={onSubmitHandler}>
                <GridContainer {...styles.gridContainer} data-test-selector="savedOrderNameModal">
                    <GridItem {...styles.gridItem}>
                        <DynamicDropdown
                            label={translate("Select Saved Order")}
                            {...styles.listsSelect}
                            isLoading={savedCartsDataView.isLoading}
                            onInputChange={listsInputChangeHandler}
                            onSelectionChange={listChangeHandler}
                            selected={selectedCartId}
                            options={options}
                            error={selectListError}
                            data-test-selector="saveCart_OrdersSelect"
                        />
                    </GridItem>
                    <GridItem {...styles.gridItem}>
                        <TextField
                            label={translate("Saved Order Name")}
                            placeholder={translate("Enter Saved Order Name")}
                            value={savedOrderName}
                            required={selectedCartId === "new"}
                            disabled={inProgress || selectedCartId !== "new"}
                            maxLength={50}
                            onChange={onChangeHandler}
                            {...styles.orderNameTextField}
                            {...{ formId }}
                        />
                    </GridItem>
                    <GridItem {...styles.buttonsGridItem}>
                        <Button
                            {...styles.cancelButton}
                            onClick={modalCloseHandler}
                            aria-label={translate("Close Modal")}
                        >
                            {translate("Cancel")}
                        </Button>
                        <Button
                            {...styles.saveButton}
                            data-test-selector="SavedOrderNameModal_save"
                            disabled={inProgress}
                            aria-label={translate("Close")}
                        >
                            {translate("Save")}
                        </Button>
                    </GridItem>
                </GridContainer>
            </StyledForm>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SavedOrderNameModal);
